/**
 * Call Stack Tracker
 *
 * Intercepts fetch and XMLHttpRequest to capture call stacks.
 * Used by resourceMonitor to identify which code initiated API calls.
 * Also tracks in-flight request count for client diagnostics.
 * Also notifies Navigation TTI tracker of network activity.
 */

import {
  incrementInFlightRequests,
  decrementInFlightRequests,
} from "./clientDiagnostics";
import { onNetworkActivity as onNavNetworkActivity } from "./navigationTTI";

export interface CallerInfo {
  caller: string;
  callerStack: string;
  httpMethod: string;
  timestamp: number;
}

/** Map of URL -> caller info for matching with PerformanceObserver */
const pendingRequests = new Map<string, CallerInfo>();

/** Clean up old entries (older than 30s) */
const cleanupPendingRequests = () => {
  const now = Date.now();
  for (const [url, info] of pendingRequests) {
    if (now - info.timestamp > 30000) {
      pendingRequests.delete(url);
    }
  }
};

/** Parse stack trace to extract meaningful caller info */
function parseStackTrace(stack: string): {
  caller: string;
  callerStack: string;
} {
  const lines = stack.split("\n").slice(1); // Skip "Error" line
  const meaningfulLines: string[] = [];

  // Patterns to skip (framework/library internals)
  const skipPatterns = [
    /callStackTracker/,
    /resourceMonitor/,
    /node_modules/,
    /webpack/,
    /chunk-vendors/,
    /<anonymous>/,
    /Function\.prototype/,
    /axios/i,
  ];

  for (const line of lines) {
    // Skip framework internals
    if (skipPatterns.some((p) => p.test(line))) continue;

    // Extract file:line from stack trace
    // Chrome: "    at functionName (file:line:col)"
    // Firefox: "functionName@file:line:col"
    const chromeMatch = line.match(
      /at\s+(?:(.+?)\s+\()?(https?:\/\/.+?):(\d+):\d+\)?/
    );
    const firefoxMatch = line.match(/(.+?)@(https?:\/\/.+?):(\d+):\d+/);

    const match = chromeMatch || firefoxMatch;
    if (match) {
      const funcName = match[1]?.trim() || "anonymous";
      const fullPath = match[2];
      const lineNum = match[3];

      // Extract just the filename from URL
      const fileName = fullPath.split("/").pop()?.split("?")[0] || fullPath;

      meaningfulLines.push(`${funcName}@${fileName}:${lineNum}`);

      // Limit to 5 meaningful frames
      if (meaningfulLines.length >= 5) break;
    }
  }

  return {
    caller: meaningfulLines[0] || "unknown",
    callerStack: meaningfulLines.join(" → "),
  };
}

/** Store caller info for a URL (deferred to avoid blocking main thread) */
function trackRequest(url: string, method: string = "GET"): void {
  try {
    // Capture stack synchronously (must be done in call context)
    const stack = new Error().stack || "";
    const httpMethod = method.toUpperCase();
    const timestamp = Date.now();

    // Defer parsing to next tick to avoid blocking main thread
    setTimeout(() => {
      try {
        const { caller, callerStack } = parseStackTrace(stack);
        pendingRequests.set(url, {
          caller,
          callerStack,
          httpMethod,
          timestamp,
        });

        // Periodic cleanup
        if (pendingRequests.size > 100) {
          cleanupPendingRequests();
        }
      } catch {
        // Silently fail
      }
    }, 0);
  } catch {
    // Silently fail
  }
}

/** Get and remove caller info for a URL */
export function getCallerInfo(url: string): CallerInfo | undefined {
  const info = pendingRequests.get(url);
  if (info) {
    pendingRequests.delete(url);
  }
  return info;
}

/** Notify Navigation TTI tracker of network activity */
function notifyTTITrackers(
  url: string,
  isStart: boolean,
  bytesTransferred?: number,
  initiatorType?: string
): void {
  try {
    onNavNetworkActivity(url, isStart, bytesTransferred, initiatorType);
  } catch {
    // Silently fail - TTI tracking is non-critical
  }
}

/**
 * Extract bytes transferred from a fetch Response
 * Uses Content-Length header if available
 */
function getBytesFromResponse(response: Response): number | undefined {
  try {
    const contentLength = response.headers.get("Content-Length");
    if (contentLength) {
      const bytes = parseInt(contentLength, 10);
      if (!isNaN(bytes) && bytes >= 0) {
        return bytes;
      }
    }
  } catch {
    // Silently fail
  }
  return undefined;
}

/** Intercept fetch to capture call stack and track in-flight requests */
function interceptFetch(): void {
  if (typeof window === "undefined" || !window.fetch) return;

  const originalFetch = window.fetch;
  window.fetch = function (
    input: Parameters<typeof fetch>[0],
    init?: Parameters<typeof fetch>[1]
  ): Promise<Response> {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
        ? input.href
        : (input as Request).url;
    // Extract method: from init, from Request object, or default to GET
    const method =
      init?.method || (input instanceof Request ? input.method : "GET");
    trackRequest(url, method);
    incrementInFlightRequests();

    // Notify TTI trackers of network activity start
    notifyTTITrackers(url, true, undefined, "fetch");

    return originalFetch.call(this, input, init).then(
      (response) => {
        decrementInFlightRequests();
        // Extract bytes transferred from response
        const bytesTransferred = getBytesFromResponse(response);
        notifyTTITrackers(url, false, bytesTransferred, "fetch");
        return response;
      },
      (error) => {
        decrementInFlightRequests();
        // No bytes available on error
        notifyTTITrackers(url, false, undefined, "fetch");
        throw error;
      }
    );
  };
}

/**
 * Extract bytes transferred from an XMLHttpRequest
 * Uses Content-Length header or response length
 */
function getBytesFromXHR(xhr: XMLHttpRequest): number | undefined {
  try {
    // Try Content-Length header first
    const contentLength = xhr.getResponseHeader("Content-Length");
    if (contentLength) {
      const bytes = parseInt(contentLength, 10);
      if (!isNaN(bytes) && bytes >= 0) {
        return bytes;
      }
    }
    // Fallback to response length for text responses
    if (xhr.responseType === "" || xhr.responseType === "text") {
      return xhr.responseText?.length;
    }
    // For arraybuffer responses
    if (xhr.responseType === "arraybuffer" && xhr.response) {
      return (xhr.response as ArrayBuffer).byteLength;
    }
    // For blob responses
    if (xhr.responseType === "blob" && xhr.response) {
      return (xhr.response as Blob).size;
    }
  } catch {
    // Silently fail
  }
  return undefined;
}

/** Intercept XMLHttpRequest to capture call stack and track in-flight requests */
function interceptXHR(): void {
  if (typeof window === "undefined" || !window.XMLHttpRequest) return;

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  // Store URL on XHR instance for TTI notification
  const xhrUrlMap = new WeakMap<XMLHttpRequest, string>();

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ): void {
    const urlStr = typeof url === "string" ? url : url.href;
    trackRequest(urlStr, method);
    xhrUrlMap.set(this, urlStr);
    return originalOpen.call(
      this,
      method,
      url,
      async ?? true,
      username,
      password
    );
  };

  XMLHttpRequest.prototype.send = function (
    body?: Parameters<typeof XMLHttpRequest.prototype.send>[0]
  ): void {
    const xhr = this;
    const url = xhrUrlMap.get(xhr) || "";
    incrementInFlightRequests();

    // Notify TTI trackers of network activity start
    notifyTTITrackers(url, true, undefined, "xmlhttprequest");

    // Use { once: true } to auto-remove listener after it fires
    // This prevents listener accumulation when XHR objects are reused
    xhr.addEventListener(
      "loadend",
      () => {
        decrementInFlightRequests();
        // Extract bytes transferred from XHR response
        const bytesTransferred = getBytesFromXHR(xhr);
        notifyTTITrackers(url, false, bytesTransferred, "xmlhttprequest");
      },
      { once: true }
    );
    return originalSend.call(xhr, body);
  };
}

/** Initialize call stack tracking by intercepting fetch and XHR */
export function initCallStackTracker(): void {
  if (typeof window === "undefined") return;
  interceptFetch();
  interceptXHR();
}
